import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { Button, FullPageLoader, FullPageStatus } from 'corso-admin-ui';
import { createContext, type PropsWithChildren, useContext } from 'react';
import api from '~/api';
import { ErrorResponse } from '~/utils/errorResponse';

export type CorsoUser = Awaited<ReturnType<typeof api.login.withAuth0>>;

const UserContext = createContext<CorsoUser | null>(null);

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(
      'UserContext unavailable. Did you forget to use an AuthenticatedUserProvider?',
    );
  }
  return context;
}

export function useIsSuperUser() {
  const superUserEmails = [
    'dallin@corso.com',
    'engineering@corso.com',
    'tanner@corso.com',
    'lcottle@corso.com',
    'logan@corso.com',
  ];
  const user = useUser();

  return superUserEmails.includes(user.email);
}

const authStatus = (error: unknown) =>
  error instanceof ErrorResponse && [401, 403].includes(error.response.status)
    ? error.response.status
    : null;

/** Resolves the Corso user for an authenticated Auth0 subject and provides it via context. */
export function AuthenticatedUserProvider({
  auth0sub,
  children,
}: { auth0sub: string } & PropsWithChildren) {
  const { logout } = useAuth0();

  const {
    data: corsoUser,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['auth0User', auth0sub],
    queryFn: () => api.login.withAuth0(),
    retry: (failureCount, queryError) =>
      !authStatus(queryError) && failureCount < 3,
    retryDelay: 1000,
  });

  if (isPending) return <FullPageLoader />;

  const status = authStatus(error);

  // 403 is a missing corsoAdmin role; 401 means the request went out without a
  // usable token, which a fresh login can fix. Either way, logging out
  // automatically would bounce straight back through loginWithRedirect, so make
  // it a click.
  if (status) {
    return (
      <FullPageStatus
        title={status === 403 ? 'Not Authorized' : 'Session Expired'}
        details={
          status === 403
            ? "Your Corso user doesn't have admin access to Command."
            : 'Please log out and log in again to continue.'
        }
      >
        <div className="flex flex-col items-center justify-center gap-4">
          <Button
            variant="primary"
            size="lg"
            onClick={() =>
              logout({ logoutParams: { returnTo: window.location.origin } })
            }
          >
            Log Out
          </Button>
        </div>
      </FullPageStatus>
    );
  }

  if (isError) {
    return (
      <FullPageStatus
        title="Unable to load your account"
        details="Your Corso user could not be found. Please contact support if this continues."
      />
    );
  }

  return (
    <UserContext.Provider value={corsoUser}>{children}</UserContext.Provider>
  );
}
